import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

function GSheetRuleModal({
  open,
  onClose,
  ruleIdx,
  onSave,
  rules,
  surveys,
  cartesian,
  icons,
  messages,
  formatMessage,
}) {
  const editing = typeof ruleIdx === 'number';
  const ruleData = editing ? rules[ruleIdx] : {};
  const [form, setForm] = useState({
    gsheetUrl: ruleData?.gsheetUrl || '',
    condition: ruleData?.condition || 'group:all,commented:false',
    survey: ruleData?.survey || '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editing) {
      setForm({
        gsheetUrl: ruleData?.gsheetUrl || '',
        condition: ruleData?.condition || 'group:all,commented:false',
        survey: ruleData?.survey || '',
      });
    } else {
      setForm({
        gsheetUrl: '',
        condition: 'group:all,commented:false',
        survey: '',
      });
    }
    setErrors({});
  }, [editing, open, ruleData?.condition, ruleData?.gsheetUrl, ruleData?.survey, ruleIdx, rules]);

  const handleChange = field => event => {
    setForm(f => ({ ...f, [field]: event.target.value }));
  };

  const handleSave = () => {
    const errs = {};
    if (!form.gsheetUrl || !form.gsheetUrl.match(/^https:\/\/docs\.google\.com\/spreadsheets\//)) {
      errs.gsheetUrl = formatMessage(messages.gsheetUrlFormat);
    }
    if (!form.survey) {
      errs.survey = formatMessage(messages.surveysRequired);
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    onSave({ ...form }, editing ? ruleIdx : undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing ? formatMessage(messages.edit) : formatMessage(messages.addRule)}
      </DialogTitle>
      <DialogContent sx={{ pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <TextField
            required
            value={form.gsheetUrl}
            onChange={handleChange('gsheetUrl')}
            error={!!errors.gsheetUrl}
            helperText={errors.gsheetUrl}
            variant="standard"
            label={formatMessage(messages.gsheetUrl)}
            placeholder="https://docs.google.com/spreadsheets/..."
            fullWidth
          />
        </FormControl>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.condition)}</InputLabel>
          <Select
            value={form.condition}
            onChange={handleChange('condition')}
            label={formatMessage(messages.condition)}
            required
            renderValue={selected => {
              const [group, commented] = selected.replace('group:', '').split(',commented:');
              const groupLabel = group.charAt(0).toUpperCase() + group.slice(1);
              const icon = icons[groupLabel] || null;
              const label = formatMessage(
                messages[`condition${commented === 'true' ? 'Commented' : 'All'}${groupLabel}`],
              );
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {icon}
                  {label}
                </span>
              );
            }}
          >
            {cartesian(['All', 'Promoters', 'Passives', 'Detractors'], [false, true]).map(
              ([group, commented]) => (
                <MenuItem
                  value={`group:${group.toLowerCase()},commented:${commented}`}
                  key={group + commented}
                >
                  <ListItemIcon>{icons[group]}</ListItemIcon>
                  {formatMessage(messages[`condition${commented ? 'Commented' : 'All'}${group}`])}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.survey)}</InputLabel>
          <Select
            value={form.survey}
            onChange={handleChange('survey')}
            label={formatMessage(messages.survey)}
            required
            renderValue={v => (surveys.find(s => s.id === v) || {}).name || ''}
            fullWidth
          >
            {surveys.map(s => (
              <MenuItem key={s.id} value={s.id}>
                <ListItemText primary={s.name} />
              </MenuItem>
            ))}
          </Select>
          {errors.survey && <div style={{ color: 'red', fontSize: 12 }}>{errors.survey}</div>}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{formatMessage(messages.cancel)}</Button>
        <Button onClick={handleSave} variant="contained">
          {formatMessage(messages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

GSheetRuleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ruleIdx: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  rules: PropTypes.array.isRequired,
  surveys: PropTypes.array.isRequired,
  messages: PropTypes.object.isRequired,
  cartesian: PropTypes.func.isRequired,
  icons: PropTypes.object.isRequired,
  formatMessage: PropTypes.func.isRequired,
};

export default GSheetRuleModal;
